/** Admin > People: everyone with an account, and the sign-up links that are still out there. */
import { Link2, ShieldCheck, UserPlus } from "lucide-react";
import { useState } from "react";
import { Avatar, Field, Modal, Spinner, type MenuItem } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { formatDate, navigate, timeAgo, toast, useSession } from "../lib/app";
import { noteAvatar } from "../lib/avatars";
import { usePictureChooser } from "../pages/ProfilePicture";
import { count, refresh, URLS, useResource, type AdminUser, type Invite, type OwnedProject, type UsersResponse } from "./data";
import { AdminLink, Card, LinkDialog, Loading, PageHead, RowMenu, useConfirm } from "./ui";

const MAX_USES = 100;

export function PeoplePage() {
  const { user, refresh: refreshSession } = useSession();
  const users = useResource<UsersResponse>(URLS.users);
  const pictures = usePictureChooser();
  const invites = useResource<{ invites: Invite[] }>(URLS.invites);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<{ title: string; url: string } | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const [filter, setFilter] = useState("");

  const list = users.data?.users ?? [];
  const inviteLimit = users.data?.inviteLimit ?? 5;
  const projectLimit = users.data?.projectLimit ?? 0;
  const nameOf = (id: number | null) => list.find((u) => u.id === id)?.username;
  const online = list.filter((u) => u.sockets > 0).length;
  const q = filter.trim().toLowerCase();
  const shown = list.filter((u) => !q || [u.username, u.displayName, u.email].some((s) => s.toLowerCase().includes(q)));
  const open = (invites.data?.invites ?? []).filter((i) => !i.usedAt);

  /** Runs an action on a user, then reloads everyone (their row and any badges). */
  async function act(url: string, body: unknown, done: string) {
    try {
      await api.post(url, body);
      toast(done, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    await Promise.all([users.reload(), invites.reload()]);
  }

  async function resetLink(u: AdminUser) {
    try {
      const r = await api.post<{ url: string }>(`/api/admin/users/${u.id}/reset`);
      setLink({ title: `Password reset link for ${u.displayName}`, url: r.url });
      invites.reload();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  /** After changing someone's picture: show the new one everywhere, your own account included if it's yours. */
  function pictureChanged(u: AdminUser, avatar: number | null) {
    noteAvatar(u.id, avatar);
    if (u.id === user?.id) refreshSession();
    users.reload();
  }

  function menu(u: AdminUser) {
    const self = u.id === user?.id;
    const items: Array<MenuItem | "-"> = [];
    if (!u.isAdmin) {
      if (!u.verified)
        items.push({
          label: "Verify",
          title: "Let them create their own projects",
          onSelect: () => act(`/api/admin/users/${u.id}/verify`, undefined, `${u.displayName} can now create projects`),
        });
      items.push(
        u.isTrusted
          ? {
              label: "Stop them inviting people",
              title: "They can no longer invite new people, and their unused invite links stop working",
              onSelect: async () => {
                const ok = await confirm({
                  title: `Remove ${u.displayName}'s trust?`,
                  body: <p>They won't be able to invite new people to their projects, and any invite links they've sent but nobody has used stop working.</p>,
                  confirm: "Remove trust",
                });
                if (ok) act(`/api/admin/users/${u.id}/trusted`, { trusted: false }, `${u.displayName} is no longer trusted`);
              },
            }
          : {
              label: "Let them invite people",
              title: "Make them trusted: they can invite people who don't have an account to their own projects",
              onSelect: () => act(`/api/admin/users/${u.id}/trusted`, { trusted: true }, `${u.displayName} can now invite up to ${inviteLimit} people`),
            },
      );
      if (u.isTrusted && u.invitesCounted)
        items.push({
          label: `Reset invite count (${u.invitesCounted} of ${inviteLimit} used)`,
          title: `Let them invite another ${inviteLimit} people`,
          onSelect: () => act(`/api/admin/users/${u.id}/invite-limit/reset`, undefined, `${u.displayName} can invite ${inviteLimit} more people`),
        });
    }
    items.push({ label: "Make a password reset link", title: "A one-time link that lets them choose a new password", onSelect: () => resetLink(u) });
    if (!self)
      items.push({
        label: "Sign out everywhere",
        title: "For a lost laptop or a shared computer. Their password still works.",
        onSelect: async () => {
          const ok = await confirm({
            title: `Sign ${u.displayName} out everywhere?`,
            body: <p>Every browser @{u.username} is signed in on is signed out, and anything they had open closes. They can sign in again with the same password.</p>,
            confirm: "Sign out everywhere",
          });
          if (ok) act(`/api/admin/users/${u.id}/sign-out`, undefined, `Signed ${u.displayName} out everywhere`);
        },
      });
    items.push({ label: "See their activity", onSelect: () => navigate(`/admin/audit?user=${encodeURIComponent(u.username)}`) });
    items.push({
      label: u.avatar ? "Change profile picture…" : "Set profile picture…",
      title: "Choose a photo and crop it for them",
      onSelect: () =>
        pictures.pick({
          name: u.displayName,
          username: u.username,
          title: `${u.displayName}'s profile picture`,
          save: async (image) => {
            const r = await api.upload<{ avatar: number }>(`/api/admin/users/${u.id}/avatar`, image);
            pictureChanged(u, r.avatar);
            toast(`Saved ${u.displayName}'s profile picture`, "success");
          },
        }),
    });
    if (u.avatar)
      items.push({
        label: "Remove profile picture",
        title: "For a picture that shouldn't be there. They can upload another.",
        onSelect: async () => {
          const ok = await confirm({
            title: `Remove ${u.displayName}'s profile picture?`,
            body: <p>Their initials show instead. They can upload a new picture from their account settings.</p>,
            confirm: "Remove picture",
          });
          if (!ok) return;
          try {
            await api.del(`/api/admin/users/${u.id}/avatar`);
            pictureChanged(u, null);
            toast(`Removed ${u.displayName}'s profile picture`, "success");
          } catch (e) {
            toast(errorMessage(e), "error");
          }
        },
      });
    if (!self) {
      items.push(
        "-",
        u.isAdmin
          ? {
              label: "Remove admin",
              onSelect: async () => {
                const ok = await confirm({
                  title: `Remove ${u.displayName} as an admin?`,
                  body: <p>They keep their account and projects, but lose the admin pages.</p>,
                  confirm: "Remove admin",
                });
                if (ok) act(`/api/admin/users/${u.id}/admin`, { isAdmin: false }, `${u.displayName} is no longer an admin`);
              },
            }
          : {
              label: "Make admin",
              onSelect: async () => {
                const ok = await confirm({
                  title: `Make ${u.displayName} an admin?`,
                  body: (
                    <p>
                      They'll see these pages, including everyone's activity and addresses in the audit log, and can manage people, projects and the server.
                    </p>
                  ),
                  confirm: "Make admin",
                });
                if (ok) act(`/api/admin/users/${u.id}/admin`, { isAdmin: true }, `${u.displayName} is now an admin`);
              },
            },
        {
          label: "Delete account…",
          danger: true,
          onSelect: async () => {
            let owned: OwnedProject[] = [];
            if (u.projects > 0) {
              try {
                owned = (await api.get<{ projects: OwnedProject[] }>(`/api/admin/users/${u.id}/projects`)).projects;
              } catch (e) {
                toast(errorMessage(e), "error");
                return;
              }
            }
            const ok = await confirm({
              title: `Delete ${u.displayName}'s account?`,
              body: (
                <>
                  <p>@{u.username} is signed out everywhere and can't sign in again. Anything in their recycle bin goes too. This can't be undone.</p>
                  {owned.length > 0 && (
                    <>
                      <p>
                        {owned.length === 1
                          ? "Their project is deleted for good as well, for everyone on it. To keep it, give it to someone else on the Projects page first."
                          : "Their projects are deleted for good as well, for everyone on them. To keep one, give it to someone else on the Projects page first."}
                      </p>
                      <ul className="admin-confirm-list">
                        {owned.map((p) => (
                          <li key={p.id}>
                            <strong>{p.name}</strong>
                            {p.sharedWith.length > 0 && <span className="muted">, shared with {p.sharedWith.join(", ")}</span>}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              ),
              confirm: owned.length ? `Delete account and ${count(owned.length, "project")}` : "Delete account",
              danger: true,
            });
            if (!ok) return;
            try {
              await api.del(`/api/admin/users/${u.id}?projects=${owned.map((p) => p.id).join(",")}`);
              toast(`Deleted ${u.username}`, "success");
            } catch (e) {
              toast(errorMessage(e), "error");
            }
            users.reload();
          },
        },
      );
    }
    return items;
  }

  return (
    <>
      <PageHead
        title="People"
        lede={
          users.data
            ? `${count(list.length, "person", "people")} with an account${online ? `, ${online} online now` : ""}. Sign-up is by invite link only.`
            : "Everyone with an account, and the links people can sign up with."
        }
        actions={
          <button className="btn btn-primary" onClick={() => setInviting(true)}>
            <UserPlus size={15} /> Invite someone
          </button>
        }
      />

      <Card
        title="Accounts"
        flush
        actions={
          list.length > 6 && (
            <input
              type="search"
              className="admin-filter"
              placeholder="Filter by name or email"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter people"
            />
          )
        }
      >
        {!users.data ? (
          users.error ? (
            <p className="error-text admin-pad">{users.error}</p>
          ) : (
            <Loading>Loading people…</Loading>
          )
        ) : (
          <div className="admin-scroll">
            <table className="table admin-table people-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Access</th>
                  <th
                    className="num"
                    title={projectLimit > 0 ? `Projects they own. People who aren't admins can own up to ${projectLimit}.` : "Projects they own"}
                  >
                    Projects
                  </th>
                  <th>Last active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Nobody matches.
                    </td>
                  </tr>
                )}
                {shown.map((u) => {
                  const atProjectLimit = !u.isAdmin && projectLimit > 0 && u.projects >= projectLimit;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="person">
                          <span className={`person-avatar${u.sockets ? " online" : ""}`}>
                            <Avatar id={u.id} name={u.displayName} size={30} />
                          </span>
                          <div className="person-text">
                            <div>
                              <b>{u.displayName}</b> <span className="muted">@{u.username}</span>
                              {u.id === user?.id && <span className="you-tag">you</span>}
                            </div>
                            <div className="muted small">{u.email || "No email"}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Access u={u} inviteLimit={inviteLimit} addedBy={nameOf(u.invitedBy)} />
                      </td>
                      <td className="num" data-label="Projects">
                        {u.isAdmin || projectLimit <= 0 ? (
                          u.projects
                        ) : (
                          <span
                            className={atProjectLimit ? "chip chip-warn" : undefined}
                            title={atProjectLimit ? "At the limit: they can't create another project" : undefined}
                          >
                            {u.projects} of {projectLimit}
                          </span>
                        )}
                      </td>
                      <td data-label="Last active">
                        {u.sockets > 0 ? (
                          <span className="online-now" title="Live connections right now, one per open project tab">
                            <i className="online-dot" /> Online <span className="muted">· {count(u.sockets, "tab")}</span>
                          </span>
                        ) : (
                          <AdminLink to={`/admin/audit?user=${encodeURIComponent(u.username)}`} className="quiet-link" title="Their activity in the audit log">
                            {u.lastSeen ? timeAgo(u.lastSeen) : "Never signed in"}
                          </AdminLink>
                        )}
                      </td>
                      <td className="row-actions">
                        {!u.verified && !u.isAdmin && (
                          <button
                            className="btn btn-sm btn-primary"
                            title="Let them create their own projects"
                            onClick={() => act(`/api/admin/users/${u.id}/verify`, undefined, `${u.displayName} can now create projects`)}
                          >
                            Verify
                          </button>
                        )}
                        <RowMenu label={`More for ${u.displayName}`} items={menu(u)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <InviteLinks
        invites={invites.data?.invites}
        error={invites.error}
        open={open.length}
        selfId={user?.id}
        nameOf={nameOf}
        onLink={setLink}
        reload={invites.reload}
        confirm={confirm}
      />

      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {link && <LinkDialog title={link.title} url={link.url} onClose={() => setLink(null)} />}
      {confirmDialog}
      {pictures.element}
    </>
  );
}

/** What someone may do, as chips: admin, trusted (and how many invites they've used), unverified. */
function Access({ u, inviteLimit, addedBy }: { u: AdminUser; inviteLimit: number; addedBy?: string }) {
  if (u.isAdmin)
    return (
      <span className="chip chip-accent" title="Can open these pages and manage everything">
        <ShieldCheck size={12} /> Admin
      </span>
    );
  const used = u.invitesCounted ?? 0;
  return (
    <div className="chips">
      {!u.verified && (
        <span className="chip chip-warn" title="Can work on projects shared with them, but can't create their own until you verify them">
          Unverified{addedBy ? ` · added by @${addedBy}` : ""}
        </span>
      )}
      {u.isTrusted && (
        <span
          className={`chip ${used >= inviteLimit ? "chip-warn" : "chip-ok"}`}
          title="Can invite people who don't have an account to their own projects. Counts people invited since their limit was last reset."
        >
          Trusted · {used} of {inviteLimit} invites used
        </span>
      )}
      {u.verified && !u.isTrusted && <span className="muted">Member</span>}
    </div>
  );
}

// ---------------------------------------------------------------- invites

/** Where an invite stands, in words. */
function inviteState(i: Invite, now = Date.now()): { open: boolean; text: string; tone?: "ok" | "warn" } {
  const several = i.maxUses > 1;
  if (i.usedAt && i.useCount >= i.maxUses) return { open: false, text: `${several ? `All ${i.maxUses} used` : "Used"} ${timeAgo(i.usedAt)}`, tone: "ok" };
  if (i.usedAt) return { open: false, text: `Revoked ${timeAgo(i.usedAt)}` };
  if (i.expiresAt < now) return { open: true, text: "Expired", tone: "warn" };
  const days = Math.ceil((i.expiresAt - now) / 86_400_000);
  return { open: true, text: days <= 1 ? "Expires today" : `Expires in ${days} days`, tone: days <= 1 ? "warn" : undefined };
}

function InviteLinks({
  invites,
  error,
  open,
  selfId,
  nameOf,
  onLink,
  reload,
  confirm,
}: {
  invites: Invite[] | undefined;
  error: string | undefined;
  open: number;
  selfId: number | undefined;
  nameOf: (id: number | null) => string | undefined;
  onLink: (l: { title: string; url: string }) => void;
  reload: () => Promise<void>;
  confirm: ReturnType<typeof useConfirm>[1];
}) {
  const [showPast, setShowPast] = useState(false);
  const all = invites ?? [];
  const past = all.filter((i) => i.usedAt);
  const shown = showPast ? all : all.filter((i) => !i.usedAt);

  async function renew(i: Invite) {
    try {
      const r = await api.post<{ url: string }>(`/api/admin/invites/${i.id}/renew`);
      onLink({ title: `New link for ${i.note || "this invite"}`, url: r.url });
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    reload();
  }

  async function revoke(i: Invite) {
    const ok = await confirm({
      title: "Revoke this link?",
      body: (
        <p>
          Nobody else can sign up with the link for {i.resetFor ? `@${i.resetFor}'s password reset` : i.note ? `“${i.note}”` : "this invite"}. People who
          already used it keep their accounts.
        </p>
      ),
      confirm: "Revoke link",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/admin/invites/${i.id}`);
      toast("Link revoked", "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    reload();
  }

  return (
    <Card
      title="Invite links"
      sub={invites ? (open ? `${count(open, "link")} not used up yet. Each one lasts 7 days.` : "No links waiting to be used.") : undefined}
      flush
      actions={
        past.length > 0 && (
          <button className="btn btn-sm btn-ghost" onClick={() => setShowPast(!showPast)}>
            {showPast ? "Hide used and revoked" : `Show used and revoked (${past.length})`}
          </button>
        )
      }
    >
      {!invites ? (
        error ? (
          <p className="error-text admin-pad">{error}</p>
        ) : (
          <Loading>Loading invites…</Loading>
        )
      ) : shown.length === 0 ? (
        <p className="admin-empty">
          <Link2 size={16} /> Links you make appear here until they're used up, so you can renew or revoke them.
        </p>
      ) : (
        <div className="admin-scroll">
          <table className="table admin-table invites-table">
            <thead>
              <tr>
                <th>For</th>
                <th>Uses</th>
                <th>Gives access to</th>
                <th>Made</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => {
                const state = inviteState(i);
                const by = i.createdBy !== selfId ? nameOf(i.createdBy) : undefined;
                return (
                  <tr key={i.id} className={state.open ? undefined : "past"}>
                    <td>
                      {i.resetFor ? (
                        <span>
                          Password reset for <b>@{i.resetFor}</b>
                        </span>
                      ) : (
                        <b>{i.note || <span className="muted">No name</span>}</b>
                      )}
                    </td>
                    <td className="nowrap" data-label="Uses">
                      {i.maxUses > 1 ? (
                        <span title={`${i.useCount} of ${i.maxUses} people have signed up with it`}>
                          {i.useCount} of {i.maxUses}
                        </span>
                      ) : (
                        <span className="muted">One person</span>
                      )}
                    </td>
                    <td className="muted" data-label="Gives access to">
                      {i.resetFor
                        ? "Their existing account"
                        : i.grants.length
                          ? i.grants.map((g) => `${g.projectName} (${g.role === "viewer" ? "view" : "edit"})`).join(", ")
                          : "A new account"}
                    </td>
                    <td className="muted nowrap" data-label="Made" title={formatDate(i.createdAt)}>
                      {timeAgo(i.createdAt)}
                      {by ? ` by @${by}` : ""}
                    </td>
                    <td className="nowrap" data-label="Status">
                      {state.tone ? <span className={`chip chip-${state.tone}`}>{state.text}</span> : <span className="muted">{state.text}</span>}
                    </td>
                    <td className="row-actions">
                      {!i.usedAt && !i.resetFor && (
                        <button className="btn btn-sm" title="Replace it with a fresh link that lasts another 7 days" onClick={() => renew(i)}>
                          New link
                        </button>
                      )}
                      {!i.usedAt && (
                        <button className="btn btn-sm btn-danger" onClick={() => revoke(i)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Makes a sign-up link for one person or a group, then shows it once. */
export function InviteDialog({ onClose }: { onClose: () => void }) {
  const [note, setNote] = useState("");
  const [uses, setUses] = useState("1");
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const n = Number(uses);
  const valid = Number.isInteger(n) && n >= 1 && n <= MAX_USES;
  const several = valid && n > 1;

  async function create() {
    setBusy(true);
    try {
      const r = await api.post<{ url: string }>("/api/admin/invites", { note, uses: n });
      setUrl(r.url);
      refresh(URLS.invites);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    setBusy(false);
  }

  if (url) {
    return (
      <LinkDialog
        title={several ? `Invite link for up to ${n} people` : note ? `Invite link for ${note}` : "Invite link"}
        url={url}
        note="it stops working after 7 days."
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title="Invite someone"
      onClose={onClose}
      width={500}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!valid || busy} onClick={create}>
            {busy && <Spinner size={12} />} Make link
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) create();
        }}
      >
        <Field
          label={several ? "What's it for?" : "Who is it for?"}
          hint={
            several
              ? "A label for this list, e.g. Thesis group. Everyone picks their own name."
              : "Their name. It becomes their display name, which they can change."
          }
        >
          <input autoFocus placeholder={several ? "e.g. Thesis group" : "e.g. Alex Chen"} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Field label="How many people can use it?" hint={`From 1 to ${MAX_USES}. Each person makes their own account.`}>
          <input type="number" min={1} max={MAX_USES} value={uses} onChange={(e) => setUses(e.target.value)} style={{ width: 110 }} />
        </Field>
        <p className="muted small" style={{ margin: 0 }}>
          To give someone a project as soon as they sign up, invite them from that project's <b>Share</b> dialog instead.
        </p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
